import { Routes } from '@angular/router';
import { OicdLogoutComponent } from './features/app-pages/oicd-logout/oicd-logout.component';
import { LayoutComponent } from './layout/components/layout/layout.component';
import { AutoLoginPartialRoutesGuard } from 'angular-auth-oidc-client';
import { LayoutResolver } from './layout/resolver/layout.resolver';
import { RoleGuard } from './core/auth/guards/role.guard';
import { PendingChangesGuard } from './core/routing/pending-changes.guard';
import { LandingPageComponent } from './layout/landing-page/landing-page.component';
import { Notfound } from './features/app-pages/notfound';
import { UnauthorizedComponent } from './features/app-pages/unauthorized';
import { VendorRequestResolver } from './features/reply-request/resolver/vendor-request.resolver';
import { CustomerRequestResolver } from './features/reply-request/resolver/customer-request.resolver';
import { AuthGuard } from './core/auth/guards/auth.guard';
import { UserRole } from './core/auth/user/enum/user-role.enum';
import { VendorsResolver } from './features/vendor/resolver/vendors.resolver';
import { VendorResolver } from './features/vendor/resolver/vendor.resolver';
import { AdminRequestResolver } from './features/reply-request/resolver/admin-request.resolver';
import { CustomerIntegrationGuard } from './core/guards/customer-integration.guard';
import { AdminIntegrationGuard } from './core/guards/admin-integration.guard';
import { ImportMarkerGuard } from './core/guards/import-marker.guard';

export const routes: Routes = [
  // --- 1) Public / unauthorisierte Routen ganz oben ---
  { path: 'unauthorized', component: UnauthorizedComponent },
  { path: 'oidc-signout', component: OicdLogoutComponent },

  // --- 2) Geschützte App-Routen im Parent mit canActivateChild ---
  {
    path: '',
    component: LayoutComponent,
    canActivate: [AuthGuard],          // initialer Check
    canActivateChild: [AuthGuard],     // auch für alle Kinder
    resolve: { initData: LayoutResolver },
    children: [
      // 2.1) Default-Redirect auf /home
      { path: '', pathMatch: 'full', redirectTo: 'home' },

      // 2.2) Hauptseiten
      {
        path: 'home',
        component: LandingPageComponent,
        canActivate: [AutoLoginPartialRoutesGuard],
      },
      // *********** Lieferanten *************
      {
        path: 'vendors',
        loadComponent: () =>
          import('./features/vendor/vendor.component').then((m) => m.VendorComponent),
        resolve: { vendors: VendorsResolver },
        canActivate: [RoleGuard],
        data: { 
          roles: [UserRole.Admin],
        },        
      },
      {
        path: 'vendors/create',
        loadComponent: () =>
          import('./features/vendor/vendor-form/vendor-form.component').then((m) => m.VendorFormComponent),
        canActivate: [RoleGuard],
        canDeactivate: [PendingChangesGuard],
          data: { 
            roles: [UserRole.Admin],
          }
      },
      {
        path: 'vendors/:id/update',
        loadComponent: () =>
          import('./features/vendor/vendor-form/vendor-form.component').then((m) => m.VendorFormComponent),
        canActivate: [RoleGuard],
        canDeactivate: [PendingChangesGuard],
        resolve: { vendor: VendorResolver },
          data: { 
            roles: [UserRole.Admin],
          }
      },
       // *********** Imports *************
      {
        path: 'imports',
        loadComponent: () =>
          import('./features/imports/imports.component').then((m) => m.ImportsComponent),
        canActivate: [RoleGuard],
        data: { 
          roles: [UserRole.Admin, UserRole.Vendor, UserRole.Customer],
        }
      },
      {
        path: 'imports/:id/protocol',
        loadComponent: () =>
          import('./features/imports/import-marker/import-marker.component').then((m) => m.ImportMarkerComponent),
        canActivate: [RoleGuard, ImportMarkerGuard],
        data: { 
          roles: [UserRole.Admin, UserRole.Vendor, UserRole.Customer],
        }
      },
       // *********** Mandanten *************
      {
        path: 'clients',
        loadComponent: () =>
          import('./features/client/client.component').then((m) => m.ClientComponent),
        canActivate: [RoleGuard],
        data: { 
          roles: [UserRole.Admin, UserRole.SystemOwner],
        }
      },
      // *********** Kunden Dashboard *************
      {
        path: 'customer-dashboard',
        loadComponent: () =>
          import('./features/customer-dashboard/customer-dashboard.component').then((m) => m.CustomerDashboardComponent),
        canActivate: [RoleGuard],
        data: { 
          roles: [UserRole.Customer],
        }
      },
      {
        path: 'customer-dashboard/:id/integration',
        loadComponent: () =>
          import('./features/customer-dashboard/integration/integration.component').then((m) => m.IntegrationComponent),
        canActivate: [RoleGuard, CustomerIntegrationGuard],
        canDeactivate: [PendingChangesGuard],
        data: {
          roles: [UserRole.Customer],
        } 
      },
             {
        path: 'customer-requests',
        loadComponent: () =>
          import('./features/customer-requests/customer-requests.component').then((m) => m.CustomerRequestsComponent),
        canActivate: [RoleGuard],
        data: {
          roles: [UserRole.Customer],
        }
      }/*, {
        path: 'customer-items',
        loadComponent: () =>
          import('./features/customer-items/customer-items.component').then((m) => m.CustomerItemsComponent),
        canActivate: [RoleGuard],
        data: { 
          roles: [UserRole.Customer],
        }
      }*/,
      {
        path: 'customer-requests/:id/reply',
        loadComponent: () =>
          import('./features/reply-request/reply-request.component').then((m) => m.ReplyRequestComponent),
        canActivate: [RoleGuard],
        resolve: { request: CustomerRequestResolver },
        data: { 
          roles: [UserRole.Customer],
        }
      },
      // *********** Lieferanten Dashboard *************
      {
        path: 'vendor-dashboard',
        loadComponent: () =>
          import('./features/vendor-dashboard/vendor-dashboard.component').then((m) => m.VendorDashboardComponent),
        canActivate: [RoleGuard],
        data: { 
          roles: [UserRole.Vendor],
        }
      },
     
      {
        path: 'vendor-requests',
        loadComponent: () =>
          import('./features/vendor-requests/vendor-requests.component').then((m) => m.VendorRequestsComponent),
        canActivate: [RoleGuard],
        data: { 
          roles: [UserRole.Vendor],
        }
      },
      {
        path: 'vendor-requests/:id/filtered',
        loadComponent: () =>
          import('./features/vendor-requests/vendor-requests.component').then((m) => m.VendorRequestsComponent),
        canActivate: [RoleGuard],
        data: { 
          roles: [UserRole.Vendor],
        }
      },
      {
        path: 'vendor-requests/:id/reply',
        loadComponent: () =>
          import('./features/reply-request/reply-request.component').then((m) => m.ReplyRequestComponent),
        canActivate: [RoleGuard],
        resolve: { request: VendorRequestResolver },
        data: { 
          roles: [UserRole.Vendor],
        }
      },{
        path: 'vendor-profile',
        loadComponent: () =>
          import('./features/vendor-profile/vendor-profile.component').then((m) => m.VendorProfileComponent),
          canActivate: [RoleGuard],
          canDeactivate: [PendingChangesGuard],
          data: { 
            roles: [UserRole.Vendor],
          }
      },{
        path: 'vendor-dashboard/integration/:id/communication-settings',
        loadComponent: () =>
          import('./features/reply-request/integration-communication-settings/integration-communication-settings.component').then((m) => m.IntegrationCommunicationSettingsComponent),
        canActivate: [RoleGuard],
        canDeactivate: [PendingChangesGuard],
        data: { 
          roles: [UserRole.Vendor],
        }
      },
      {
        path: 'vendors/:id/integrations/:integrationId',
        loadComponent: () => 
          import('./features/vendor/admin-integration/admin-integration/admin-integration.component').then((m) => m.AdminIntegrationComponent),
        canActivate: [RoleGuard, AdminIntegrationGuard],
        canDeactivate: [PendingChangesGuard],
        data: {
          roles: [UserRole.Admin],
        }
      },
      // *********** Admin Requests *************
       {
        path: 'admin-requests',
        loadComponent: () =>
          import('./features/admin-requests/admin-requests.component').then((m) => m.AdminRequestsComponent),
        canActivate: [RoleGuard],
        data: { 
          roles: [UserRole.Admin, UserRole.SystemOwner],
        }
      },
      {
        path: 'admin-requests/:id/reply',
        loadComponent: () =>
          import('./features/reply-request/reply-request.component').then((m) => m.ReplyRequestComponent),
        canActivate: [RoleGuard],
        resolve: { request: AdminRequestResolver },
        data: { 
          roles: [UserRole.Admin, UserRole.SystemOwner],
        }
      },       {
        path: 'admin-systems',
        loadComponent: () =>
          import('./features/admin-systems/admin-systems.component').then((m) => m.AdminSystemsComponent),
        canActivate: [RoleGuard],
        data: { 
          roles: [UserRole.Admin, UserRole.SystemOwner],
        }
      },       {
        path: 'admin-items',
        loadComponent: () =>
          import('./features/admin-items/admin-items.component').then((m) => m.AdminItemsComponent),
        canActivate: [RoleGuard],
        data: { 
          roles: [UserRole.Admin, UserRole.SystemOwner],
        }
      },       {
        path: 'users',
        loadComponent: () =>
          import('./features/users/users.component').then((m) => m.UsersComponent),
        canActivate: [RoleGuard],
        data: { 
          roles: [UserRole.Admin],
        }
      },       {
        path: 'admin-systems/:systemId/client-applications',
        loadComponent: () =>
          import('./features/admin-systems/admin-client-application/admin-client-application.component').then((m) => m.AdminClientApplicationComponent),
        canActivate: [RoleGuard],
        data: { 
          roles: [UserRole.Admin, UserRole.SystemOwner],
        }
      },
      {
        path: 'coming-soon',
        loadComponent: () =>
          import('./features/app-pages/coming-soon').then((m) => m.ComingSoonComponent),
      },
      {
        path: 'unauthorized-role',
        loadComponent: () =>
          import('./features/app-pages/no-role').then((m) => m.NoRoleComponent),
      },

      // 2.3) Catch-all für nicht gefundene Kinder
      { path: '**', redirectTo: 'home' }
    ]
  },

  // --- 3) Globaler Catch-all: alle nicht abgefangenen Routen gehen auf „Not Found“ ---
  { path: 'notfound', component: Notfound },
  { path: '**', redirectTo: 'notfound' }
];



/***Breadcrumb möglichkeiten 
 
  statisch:
  
  data: {
  breadcrumb: [
    { label: 'Home', url: '/' },
    { label: 'Produkte' }
  ]
}

dynamisch via resolver:
data: {
  resolve: { item: ItemResolver },
  breadcrumb: [
    { label: 'Produkte', url: '/products' },
    { label: (d) => d.item.name }
  ]
}


per state:

In der navigation den State mitgenben
this.router.navigate(['/foo'], {
  state: { breadcrumbName: {{foo.bar}} }
});

breadcrumb: [
    { label: 'Home', url: '/' },
    { label: undefined }
  ]

entsprechendes Label auf undefined setzen, damit es aus dem State geholt wird




 * 
*/
